import { useState, useEffect, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Loader2 } from "lucide-react";

// Password rules mirror the backend's ResetPasswordRequest validator.
// Keeping them in sync here catches mistakes before a round-trip.
function passwordIssue(pw: string): string | null {
  if (pw.length < 8) return "Password must be at least 8 characters.";
  if (!/[A-Z]/.test(pw)) return "Password must contain an uppercase letter.";
  if (!/[a-z]/.test(pw)) return "Password must contain a lowercase letter.";
  if (!/\d/.test(pw)) return "Password must contain a digit.";
  return null;
}

export default function ResetPassword() {
  useEffect(() => {
    document.title = "Reset Password — PREP POS";
  }, []);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPwError(null);
    setConfirmError(null);
    setFormError(null);

    if (!token) {
      setFormError("This reset link is missing a token. Request a new one.");
      return;
    }

    const issue = passwordIssue(password);
    if (issue) {
      setPwError(issue);
      return;
    }

    if (password !== confirm) {
      setConfirmError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await apiClient.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      toast({
        title: "Password updated",
        description: "You can now sign in with your new password.",
      });
      navigate("/login", { replace: true });
    } catch {
      setFormError(
        "This reset link is invalid or has expired. Please request a new one.",
      );
    } finally {
      setLoading(false);
    }
  };

  const liveHint = password ? passwordIssue(password) : null;

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center space-y-4">
          <p className="text-destructive font-medium">
            Missing reset token. This link may have been truncated.
          </p>
          <Link
            to="/forgot-password"
            className="text-primary hover:underline text-sm"
          >
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-foreground font-serif">
            Choose a new password
          </h1>
          <p className="text-muted-foreground">
            Must be 8+ characters with an uppercase, lowercase, and digit.
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 shadow-sm space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  aria-invalid={Boolean(pwError)}
                  aria-describedby={
                    pwError
                      ? "rp-password-error"
                      : liveHint
                        ? "rp-password-hint"
                        : undefined
                  }
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </div>
              {pwError ? (
                <p id="rp-password-error" role="alert" className="text-xs text-destructive">
                  {pwError}
                </p>
              ) : password && liveHint ? (
                <p id="rp-password-hint" className="text-xs text-muted-foreground">
                  {liveHint}
                </p>
              ) : password ? (
                <p className="text-xs text-emerald-600">
                  Password meets the minimum complexity requirements.
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input
                id="confirm"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                aria-invalid={Boolean(confirmError)}
                aria-describedby={confirmError ? "rp-confirm-error" : undefined}
                className="h-11"
              />
              {confirmError ? (
                <p id="rp-confirm-error" role="alert" className="text-xs text-destructive">
                  {confirmError}
                </p>
              ) : null}
            </div>

            {formError ? (
              <p role="alert" aria-live="polite" className="text-sm text-destructive">
                {formError}
              </p>
            ) : null}

            <Button
              type="submit"
              className="w-full h-12 text-base bg-primary hover:bg-primary/90"
              disabled={loading}
            >
              {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
              Update password
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="font-medium text-primary hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
